import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import AppLayout from "../components/AppLayout";
import VideoThumbnail from "../components/VideoThumbnail";
import { Icons } from "../lib/Icons";

// Domain configuration with colors
const DOMAINS = [
  { 
    id: 'Communication', 
    label: 'Communication', 
    Icon: Icons.ChatBubble,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    selectedColor: 'bg-blue-600 text-white border-blue-600'
  },
  { 
    id: 'SEMH', 
    label: 'Social & Emotional', 
    Icon: Icons.Heart,
    color: 'bg-green-50 text-green-700 border-green-200',
    selectedColor: 'bg-green-600 text-white border-green-600'
  },
  { 
    id: 'Cognition', 
    label: 'Cognition & Learning', 
    Icon: Icons.Brain,
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    selectedColor: 'bg-purple-600 text-white border-purple-600'
  },
  { 
    id: 'Sensory/Physical', 
    label: 'Sensory & Physical', 
    Icon: Icons.Hand,
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    selectedColor: 'bg-orange-600 text-white border-orange-600'
  },
];

// Age groups - matching solo dashboard
const AGE_GROUPS = [
  { id: 'eyfs', label: 'EYFS', description: 'Ages 0-4', ageMin: 0, ageMax: 4 },
  { id: 'primary', label: 'Primary', description: 'Ages 4-11', ageMin: 4, ageMax: 11 },
  { id: 'secondary', label: 'Secondary', description: 'Ages 11-16', ageMin: 11, ageMax: 16, hasSubOptions: true },
  { id: 'all', label: 'All Ages', description: 'All resources', ageMin: 0, ageMax: 19 },
];

// Key stage sub-options for secondary
const KEY_STAGE_OPTIONS = [
  { id: 'ks3', label: 'Key Stage 3', description: 'Years 7-9', ageMin: 11, ageMax: 14, keyStage: 'ks3' },
  { id: 'ks4', label: 'Key Stage 4', description: 'Years 10-11', ageMin: 14, ageMax: 16, keyStage: 'ks4' },
];

// Activity type configuration
const ACTIVITY_TYPES = [
  { id: 'structured', label: 'Structured Sessions', description: 'Dedicated session activities', Icon: Icons.Clock },
  { id: 'ongoing', label: 'Ongoing Strategies', description: 'Embedded throughout the day', Icon: Icons.ArrowPath },
];

// Popular tags for browsing
const POPULAR_TAGS = [
  'sensory', 'communication', 'fine-motor', 'calming', 'visual', 
  'social-skills', 'attention', 'sequencing', 'regulation', 'aac'
];

// Quick search suggestions
const QUICK_SEARCHES = ['PECS', 'Sensory', 'Makaton', 'Attention Autism', 'Zones of Regulation', 'Social Stories'];

export default function ResourceLibraryPage() {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedDomain, setSelectedDomain] = useState(searchParams.get('domain') || '');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(searchParams.get('age') || 'all');
  const [selectedKeyStage, setSelectedKeyStage] = useState(searchParams.get('ks') || '');
  const [selectedActivityType, setSelectedActivityType] = useState(searchParams.get('type') || '');
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [showInterventionsOnly, setShowInterventionsOnly] = useState(searchParams.get('interventions') === 'true');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadActivities();
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedDomain) params.set('domain', selectedDomain);
    if (selectedAgeGroup && selectedAgeGroup !== 'all') params.set('age', selectedAgeGroup);
    if (selectedKeyStage) params.set('ks', selectedKeyStage);
    if (selectedActivityType) params.set('type', selectedActivityType);
    if (selectedTag) params.set('tag', selectedTag);
    if (showInterventionsOnly) params.set('interventions', 'true');
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedDomain, selectedAgeGroup, selectedKeyStage, selectedActivityType, selectedTag, showInterventionsOnly, setSearchParams]);

  // Clear key stage selection when age group changes from secondary
  useEffect(() => {
    if (selectedAgeGroup !== 'secondary') {
      setSelectedKeyStage('');
    }
  }, [selectedAgeGroup]);

  const loadActivities = async () => {
    try {
      const kbRef = collection(db, 'kb_items');
      const snapshot = await getDocs(kbRef);
      
      const allActivities = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        allActivities.push({
          id: doc.id,
          ...data,
        });
      });

      setActivities(allActivities);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Basic filtering logic
  const filteredActivities = useMemo(() => {
    let results = [...activities];

    // Text search (title, description, tags)
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      results = results.filter(activity => {
        const titleMatch = activity.title?.toLowerCase().includes(query);
        const descMatch = activity.description?.toLowerCase().includes(query);
        const tagMatch = activity.tags?.some(tag => tag.toLowerCase().includes(query));
        const domainMatch = activity.targetDomains?.some(d => d.toLowerCase().includes(query));
        return titleMatch || descMatch || tagMatch || domainMatch;
      });
    }

    // Domain filter
    if (selectedDomain) {
      results = results.filter(activity => 
        activity.targetDomains?.includes(selectedDomain)
      );
    }

    // Age group filter - matching solo dashboard logic
    if (selectedAgeGroup && selectedAgeGroup !== 'all') {
      results = results.filter(activity => {
        // Check phases array first (new format supports multiple phases)
        if (activity.phases && Array.isArray(activity.phases)) {
          return activity.phases.includes(selectedAgeGroup);
        }

        // Backward compatibility: check legacy phase field
        if (activity.phase) {
          if (activity.phase === 'both') {
            // 'both' means primary and eyfs (legacy behavior)
            return selectedAgeGroup === 'primary' || selectedAgeGroup === 'eyfs';
          }
          return activity.phase === selectedAgeGroup;
        }

        // Fallback: use age range to determine eligibility (overlap check)
        const minAge = activity.ageRange?.min || 0;
        const maxAge = activity.ageRange?.max || 19;

        if (selectedAgeGroup === 'eyfs') {
          return minAge <= 4 && maxAge >= 0; // Overlaps with EYFS range (0-4)
        } else if (selectedAgeGroup === 'primary') {
          return minAge <= 11 && maxAge >= 4; // Overlaps with Primary range (4-11)
        } else if (selectedAgeGroup === 'secondary') {
          return minAge <= 16 && maxAge >= 11; // Overlaps with Secondary range (11-16)
        }

        return true;
      });
    }

    // Key Stage filter (for secondary sub-options)
    if (selectedKeyStage && selectedAgeGroup === 'secondary') {
      const ksOption = KEY_STAGE_OPTIONS.find(ks => ks.id === selectedKeyStage);
      if (ksOption) {
        results = results.filter(activity => {
          // Check if activity has keyStages field
          if (activity.keyStages && Array.isArray(activity.keyStages)) {
            return activity.keyStages.includes(ksOption.keyStage);
          }
          // Fallback to age range
          const minAge = activity.ageRange?.min || 0;
          const maxAge = activity.ageRange?.max || 19;
          return minAge <= ksOption.ageMax && maxAge >= ksOption.ageMin;
        });
      }
    }

    // Activity type filter
    if (selectedActivityType) {
      results = results.filter(activity => activity.activityType === selectedActivityType);
    }

    // Tag filter
    if (selectedTag) {
      results = results.filter(activity => 
        activity.tags?.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
      );
    }

    // Interventions filter
    if (showInterventionsOnly) {
      results = results.filter(activity => activity.isIntervention === true);
    }

    // Sorting
    if (sortBy === 'title') {
      results.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'newest') {
      results.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(0);
        const bDate = b.createdAt?.toDate?.() || new Date(0);
        return bDate - aDate;
      });
    }

    return results;
  }, [activities, searchQuery, selectedDomain, selectedAgeGroup, selectedKeyStage, selectedActivityType, selectedTag, showInterventionsOnly, sortBy]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDomain('');
    setSelectedAgeGroup('all');
    setSelectedKeyStage('');
    setSelectedActivityType('');
    setSelectedTag('');
    setShowInterventionsOnly(false);
  };

  const hasActiveFilters = searchQuery || selectedDomain || (selectedAgeGroup && selectedAgeGroup !== 'all') || selectedKeyStage || selectedActivityType || selectedTag || showInterventionsOnly;

  const handleSearch = (e) => {
    e.preventDefault();
  };

  // Get domain info for styling activity cards
  const getDomainInfo = (domainId) => {
    return DOMAINS.find(d => d.id === domainId) || DOMAINS[0];
  };

  // Helper to extract YouTube video ID
  const extractVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com(?:\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=))|youtu\.be\/)([^"&?/\s]{11})/);
    return match ? match[1] : null;
  };

  // Helper to get video thumbnail and id from activity (for VideoThumbnail 404 handling)
  const getVideoThumbnailInfo = (activity) => {
    const resources = activity.resources;
    if (!Array.isArray(resources)) return null;
    const video = resources.find(r => r.kind === 'video');
    if (!video) return null;
    const videoId = extractVideoId(video.baseUrl);
    if (!videoId && !video.meta?.thumbnail) return null;
    return { src: video.meta?.thumbnail || null, videoId: videoId || null };
  };

  // Helper to count videos
  const getVideoCount = (activity) => {
    const resources = activity.resources;
    if (!Array.isArray(resources)) return 0;
    return resources.filter(r => r.kind === 'video').length;
  };

  // Helper to check for downloadable resources
  const hasDownloadableResources = (activity) => {
    return activity.downloadableResources?.types?.length > 0;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading resources...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Icons.BookOpen className="h-7 w-7 text-blue-600" />
                </div>
                Resource Library
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Browse {activities.length} evidence-based activities, strategies, and resources
              </p>
            </div>
            
            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="sm:hidden inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Icons.Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                  Active
                </span>
              )}
            </button>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mt-6">
            <div className="relative max-w-2xl">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources by name, description, or tag..."
                className="w-full px-4 py-3 pl-11 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              />
              <Icons.MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icons.XMark className="w-5 h-5" />
                </button>
              )}
            </div>
          </form>

          {/* Quick Search Suggestions */}
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-sm text-gray-500">Quick search:</span>
            {QUICK_SEARCHES.map(term => (
              <button
                key={term}
                onClick={() => setSearchQuery(term)}
                className="px-3 py-1 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                {term}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Domain Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Domain</label>
                <div className="space-y-2">
                  {DOMAINS.map(domain => (
                    <button
                      key={domain.id}
                      onClick={() => setSelectedDomain(selectedDomain === domain.id ? '' : domain.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-sm ${
                        selectedDomain === domain.id
                          ? domain.selectedColor
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <domain.Icon className="w-4 h-4" />
                      <span>{domain.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Age Group</label>
                <div className="space-y-2">
                  {AGE_GROUPS.map(group => (
                    <div key={group.id}>
                      <button
                        onClick={() => setSelectedAgeGroup(group.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all border ${
                          selectedAgeGroup === group.id
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="font-medium">{group.label}</span>
                        <span className="ml-2 text-xs opacity-75">({group.description})</span>
                      </button>
                      
                      {/* Show Key Stage sub-options when Secondary is selected */}
                      {group.hasSubOptions && selectedAgeGroup === group.id && (
                        <div className="mt-2 ml-3 space-y-1.5 border-l-2 border-blue-200 pl-3">
                          {KEY_STAGE_OPTIONS.map(ks => (
                            <button
                              key={ks.id}
                              onClick={() => setSelectedKeyStage(selectedKeyStage === ks.id ? '' : ks.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all border ${
                                selectedKeyStage === ks.id
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              <span className="font-medium">{ks.label}</span>
                              <span className="ml-1 opacity-75">({ks.description})</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Activity Type Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Activity Type</label>
                <div className="space-y-2">
                  {ACTIVITY_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedActivityType(selectedActivityType === type.id ? '' : type.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-sm ${
                        selectedActivityType === type.id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <type.Icon className={`w-4 h-4 ${selectedActivityType === type.id ? 'text-white' : 'text-gray-400'}`} />
                      <div className="flex-1 text-left">
                        <span className="font-medium">{type.label}</span>
                        <p className={`text-xs mt-0.5 ${selectedActivityType === type.id ? 'text-blue-100' : 'text-gray-400'}`}>
                          {type.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Interventions Toggle */}
              <div className="mb-6">
                <button
                  onClick={() => setShowInterventionsOnly(!showInterventionsOnly)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border transition-all ${
                    showInterventionsOnly
                      ? 'bg-amber-50 text-amber-800 border-amber-300'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Icons.Target className={`w-5 h-5 ${showInterventionsOnly ? 'text-amber-600' : 'text-gray-400'}`} />
                  <div className="flex-1 text-left">
                    <span className="text-sm font-medium">Interventions Only</span>
                    {showInterventionsOnly && (
                      <p className="text-xs text-amber-600 mt-0.5">Showing evidence-based interventions</p>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded flex items-center justify-center transition-all ${
                    showInterventionsOnly ? 'bg-amber-500' : 'bg-gray-200'
                  }`}>
                    {showInterventionsOnly && <Icons.Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="relevance">Relevance</option>
                  <option value="title">Title A-Z</option>
                  <option value="newest">Newest First</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1 min-w-0">
            {/* Popular Tags Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icons.Tag className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Browse by Tag</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {POPULAR_TAGS.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedTag === tag
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Results header */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">{filteredActivities.length}</span>
                {' '}{filteredActivities.length === 1 ? 'resource' : 'resources'}
                {searchQuery && <span className="text-gray-400"> matching "{searchQuery}"</span>}
              </p>
              
              {/* Active filter chips */}
              {hasActiveFilters && (
                <div className="hidden sm:flex flex-wrap gap-2">
                  {selectedDomain && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {DOMAINS.find(d => d.id === selectedDomain)?.label}
                      <button onClick={() => setSelectedDomain('')}>
                        <Icons.XMark className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {selectedAgeGroup && selectedAgeGroup !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.label}
                      {selectedKeyStage && ` - ${KEY_STAGE_OPTIONS.find(k => k.id === selectedKeyStage)?.label}`}
                      <button onClick={() => { setSelectedAgeGroup('all'); setSelectedKeyStage(''); }}>
                        <Icons.XMark className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {selectedActivityType && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {ACTIVITY_TYPES.find(t => t.id === selectedActivityType)?.label}
                      <button onClick={() => setSelectedActivityType('')}>
                        <Icons.XMark className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {selectedTag && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      #{selectedTag}
                      <button onClick={() => setSelectedTag('')}>
                        <Icons.XMark className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {showInterventionsOnly && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                      Interventions
                      <button onClick={() => setShowInterventionsOnly(false)}>
                        <Icons.XMark className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Results Grid */}
            {filteredActivities.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Icons.MagnifyingGlass className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No resources found</h3>
                <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                  Try adjusting your search or filters to find what you're looking for
                </p>
                <button
                  onClick={clearFilters}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filteredActivities.map(activity => {
                  const primaryDomain = activity.targetDomains?.[0];
                  const domainInfo = getDomainInfo(primaryDomain);
                  const videoThumb = getVideoThumbnailInfo(activity);
                  const videoCount = getVideoCount(activity);
                  const hasWorksheets = hasDownloadableResources(activity);

                  return (
                    <Link
                      key={activity.id}
                      to={`/solo/activity/${activity.id}`}
                      className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-md transition-all"
                    >
                      {/* Card Header - Video thumbnail or domain gradient */}
                      {videoThumb ? (
                        <div className="relative h-32 overflow-hidden">
                          <VideoThumbnail
                            src={videoThumb.src}
                            videoId={videoThumb.videoId}
                            alt={activity.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          {/* Video count badge */}
                          {videoCount > 0 && (
                            <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/70 text-white text-xs font-medium rounded">
                              <Icons.Film className="w-3 h-3" />
                              {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                            </div>
                          )}
                          {/* Worksheet badge */}
                          {hasWorksheets && (
                            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded">
                              <Icons.Document className="w-3 h-3" />
                              Worksheets
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={`h-24 relative ${
                          primaryDomain === 'Communication' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          primaryDomain === 'SEMH' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                          primaryDomain === 'Cognition' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                          primaryDomain === 'Sensory/Physical' ? 'bg-gradient-to-br from-orange-500 to-orange-600' :
                          'bg-gradient-to-br from-gray-400 to-gray-500'
                        }`}>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <domainInfo.Icon className="w-10 h-10 text-white/30" />
                          </div>
                          {/* Worksheet badge for non-video cards */}
                          {hasWorksheets && (
                            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur text-white text-xs font-medium rounded">
                              <Icons.Document className="w-3 h-3" />
                              Worksheets
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="p-5">
                        {/* Domain tags */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {activity.targetDomains?.slice(0, 2).map(domain => {
                            const info = getDomainInfo(domain);
                            return (
                              <span
                                key={domain}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${info.color} border`}
                              >
                                <info.Icon className="w-3 h-3" />
                                {domain}
                              </span>
                            );
                          })}
                          {activity.isIntervention && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-medium">
                              <Icons.Target className="w-3 h-3" />
                              Intervention
                            </span>
                          )}
                        </div>
                        
                        <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {activity.title}
                        </h3>
                        <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                          {activity.description}
                        </p>
                        
                        {/* Meta info */}
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          {activity.ageRange && (
                            <span className="flex items-center gap-1">
                              <Icons.Users className="w-3.5 h-3.5" />
                              Ages {activity.ageRange.min}-{activity.ageRange.max}
                            </span>
                          )}
                          {activity.keyStages && activity.keyStages.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Icons.AcademicCap className="w-3.5 h-3.5" />
                              {activity.keyStages.map(ks => ks.toUpperCase()).join('/')}
                            </span>
                          )}
                          {activity.resources && Array.isArray(activity.resources) && activity.resources.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Icons.Film className="w-3.5 h-3.5" />
                              {activity.resources.length} videos
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Load more / pagination hint */}
            {filteredActivities.length > 0 && filteredActivities.length >= 12 && (
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-500">
                  Showing {filteredActivities.length} resources
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </AppLayout>
  );
}
